import iconStatusSuccess from "assets/img/icon-status-success.svg"
import iconStatusError from "assets/img/icon-status-error.svg"

import React, { FunctionComponent } from "react"
import { useTranslation } from "react-i18next"

import { Flex } from "components/Flex"
import Text from "components/Text/Text"
import Button from "components/Button/Button"
import Spinner from "components/Spinner/Spinner"
import Lmid from "components/Lmid/Lmid"
import ValidationMessage from "components/ValidationMessage/ValidationMessage"
import AssetStatusTag from "components/AssetStatusTag/AssetStatusTag"
import { AssetOwnershipType } from "models/Asset"
import { LmidModalMode } from "components/LmidModal/lmidModalMode"
import { Guid } from "@/types"
import {
    CopyAssetSharedKey,
    ICopyAssetRow,
    ICopyAssetSourceValues,
    ICopyAssetState,
    effectiveValue,
    needsOwnerInput,
} from "./copyAssetRows"
import { COPY_ASSET_TEXT_FIELDS, ownershipAmountField } from "./copyAssetFieldDefs"
import {
    CopyAssetCreationStatus,
    ICopyAssetCreationResult,
} from "./useCopyAssetCreation"

export interface ICopyAssetResultsProps {
    state: ICopyAssetState
    sourceLmid: string
    sourceAssetId?: Guid
    sourceValues: ICopyAssetSourceValues
    /** Currency shown after the amount values, keyed by shared value. */
    amountCurrencies: Partial<Record<CopyAssetSharedKey, string>>
    resultsByRowId: Record<string, ICopyAssetCreationResult>
    isRunning: boolean
    onRetry(rowId: string): void
}

interface IResultField {
    labelKey: string
    sourceValue: string
    /** Currency appended to the displayed values of an amount field. */
    currency?: string
    valueOf(row: ICopyAssetRow): string
}

function shortened(value: string): string {
    return value.length > 60 ? `${value.slice(0, 60)}…` : value
}

/**
 * Per-copy results while and after the copies are created. The source asset's
 * values head the list; each copy line shows a spinner while it is in
 * progress, then its outcome icon, the new LMID (opening the copy's details
 * sidebar) with its status on success, the fields in which the copy differs
 * from the source, and the error with a retry action on failure.
 */
const CopyAssetResults: FunctionComponent<React.PropsWithChildren<ICopyAssetResultsProps>> = ({
    state,
    sourceLmid,
    sourceAssetId,
    sourceValues,
    amountCurrencies,
    resultsByRowId,
    isRunning,
    onRetry,
}) => {
    const { t } = useTranslation()

    function rowLabel(rowIndex: number): string {
        const row = state.rows[rowIndex]
        if (row.lmid) return row.lmid
        if (row.lmidMode === LmidModalMode.enterLmidLater) return t('copyAsset.savedWithoutLmid')
        return t('copyAsset.assignedOnSave')
    }

    function sourceString(value: string | number | null | undefined): string {
        return value == null ? "" : String(value)
    }

    const amountField = ownershipAmountField(state.ownershipType === AssetOwnershipType.borrowed)
    const resultFields: IResultField[] = [
        {
            labelKey: "asset.serialNumber",
            sourceValue: sourceString(sourceValues.serialNumber),
            valueOf: row => row.serialNumber,
        },
        {
            labelKey: "copyAsset.fields.serialNumberMachine",
            sourceValue: sourceString(sourceValues.serialNumberMachine),
            valueOf: row => row.serialNumberMachine,
        },
        {
            labelKey: "copyAsset.fields.inventoryNumber",
            sourceValue: sourceString(sourceValues.inventoryNo),
            valueOf: row => row.inventoryNo,
        },
        ...COPY_ASSET_TEXT_FIELDS.map(field => ({
            labelKey: field.labelKey,
            sourceValue: sourceString(sourceValues[field.key]),
            valueOf: (row: ICopyAssetRow) => effectiveValue(state, row, field.key),
        })),
        {
            labelKey: amountField.labelKey,
            sourceValue: sourceString(sourceValues[amountField.key]),
            currency: amountCurrencies[amountField.key],
            valueOf: (row: ICopyAssetRow) => effectiveValue(state, row, amountField.key),
        },
        ...(needsOwnerInput(state)
            ? [{
                labelKey: "copyAsset.fields.companyWhereBorrowedAsset",
                sourceValue: "",
                valueOf: (row: ICopyAssetRow) => effectiveValue(state, row, "ownerName"),
            }]
            : []),
    ]

    function rowDiffs(row: ICopyAssetRow): IResultField[] {
        return resultFields.filter(field => field.valueOf(row).trim() !== field.sourceValue.trim())
    }

    function displayValue(field: IResultField, value: string): string {
        if (value.trim() === "") return t('common.noValue')
        return field.currency ? `${shortened(value)} ${field.currency}` : shortened(value)
    }

    return (
        <div className="copy-asset__results border-grey-100 bg-white round-sm" data-testid="copy-asset-results">
            <div className="px-2 py-1 bg-grey-50 border-bottom-grey-200" data-testid="copy-asset-results-source">
                <Flex alignItems="center" gap="1.6rem">
                    <Text type="p" className="font-bold">{t('copyAsset.sourceAsset')}</Text>
                    <Flex alignItems="center" gap="0.4rem">
                        <Text type="p">{t('common.lmid')}:</Text>
                        <Lmid lmid={sourceLmid} assetId={sourceAssetId} />
                    </Flex>
                    <AssetStatusTag status={sourceValues.status} />
                </Flex>
                <div className="copy-asset__result-diffs mt-1">
                    {resultFields.map(field => (
                        <Text key={field.labelKey} type="pSmallest" className="color-grey-500">
                            {t(field.labelKey)}: {displayValue(field, field.sourceValue)}
                        </Text>
                    ))}
                </div>
            </div>
            {state.rows.map((row, index) => {
                const result = resultsByRowId[row.id]
                const status = result?.status ?? CopyAssetCreationStatus.pending
                const diffs = rowDiffs(row)
                return (
                    <div
                        key={row.id}
                        className="copy-asset__result px-2 py-1 border-bottom-grey-200"
                        data-testid={`copy-asset-result-${index + 1}`}>
                        <span className="copy-asset__number">{index + 1}</span>
                        <span className="copy-asset__result-status">
                            {(status === CopyAssetCreationStatus.creating || (isRunning && status === CopyAssetCreationStatus.pending)) &&
                                <Spinner size={20} />
                            }
                            {status === CopyAssetCreationStatus.success &&
                                <img
                                    src={iconStatusSuccess}
                                    alt=""
                                    className="d-block"
                                    data-testid={`copy-asset-result-success-${index + 1}`}
                                />
                            }
                            {status === CopyAssetCreationStatus.failed &&
                                <img src={iconStatusError} alt="" className="d-block" />
                            }
                        </span>
                        <Text type="p" className="font-bold">
                            {t('copyAsset.copyNofM', { number: index + 1, total: state.rows.length })}
                        </Text>
                        {status === CopyAssetCreationStatus.success && result?.assetId
                            ? <Lmid lmid={result.lmid} assetId={result.assetId} />
                            : <Text type="p" className="color-grey-500">{rowLabel(index)}</Text>
                        }
                        <span>
                            <AssetStatusTag status={effectiveValue(state, row, "status")} />
                        </span>
                        <div className="copy-asset__result-details">
                            {diffs.length > 0
                                ? <div className="copy-asset__result-diffs">
                                    {diffs.map(field => (
                                        <Text key={field.labelKey} type="pSmallest" className="color-grey-500">
                                            {t(field.labelKey)}:{" "}
                                            <span className="font-bold color-black">{displayValue(field, field.valueOf(row))}</span>
                                        </Text>
                                    ))}
                                </div>
                                : <Text type="pSmallest" className="color-grey-500">{t('copyAsset.sameAsSource')}</Text>
                            }
                            {status === CopyAssetCreationStatus.failed && !!result?.errorMessage &&
                                <ValidationMessage>{result.errorMessage}</ValidationMessage>
                            }
                        </div>
                        <span>
                            {status === CopyAssetCreationStatus.failed &&
                                <Button
                                    theme="secondary"
                                    size="small"
                                    label={t('copyAsset.retry')}
                                    disabled={isRunning}
                                    onClick={() => onRetry(row.id)}
                                    data-testid={`copy-asset-retry-${index + 1}`}
                                />
                            }
                        </span>
                    </div>
                )
            })}
        </div>
    )
}

export default CopyAssetResults
